import { baseCodeApi } from '@/shared/api/baseCodeApi';

export interface CodeSelectOption {
  title: string;
  value: string;
  bigo3?: string;
}

let countryOptionsCache: CodeSelectOption[] | null = null;
let countryLoadPromise: Promise<CodeSelectOption[]> | null = null;

function trim(v: unknown): string {
  return v === null || v === undefined ? '' : String(v).trim();
}

export async function loadCountryCodeOptions(): Promise<CodeSelectOption[]> {
  if (countryOptionsCache) return countryOptionsCache;
  if (countryLoadPromise) return countryLoadPromise;

  countryLoadPromise = (async () => {
    const options: CodeSelectOption[] = [];
    try {
      const response = await baseCodeApi.getCodesByMainCode('CM', 'B05', undefined);
      const codes = (response as { codes?: unknown[] }).codes
        ?? (response as { data?: { codes?: unknown[] } }).data?.codes
        ?? [];
      for (const code of codes as Record<string, unknown>[]) {
        const value = trim(code.totalCode ?? code.TOTAL_CODE);
        const codeName = trim(code.codeName ?? code.CODE_NAME);
        const defaultCodeName = trim(code.defaultCodeName ?? code.DEFAULT_CODE_NAME);
        const bigo3 = trim(code.bigo3 ?? code.BIGO3);
        let title = defaultCodeName || codeName;
        const withPlus = [codeName, defaultCodeName].find((n) => /\+\d/.test(n));
        if (withPlus) title = withPlus;
        else if (bigo3 && title && !title.includes(bigo3)) title = `${title} ${bigo3}`.trim();
        if (value && title) options.push({ title, value, bigo3 });
      }
    } catch (e) {
      console.warn('[notice-corporate formOptions] B05 load failed:', e);
    }
    countryOptionsCache = options;
    return options;
  })();

  return countryLoadPromise;
}

export function resolveCountryCdToLabel(
  countryCd: string,
  options?: CodeSelectOption[],
): string {
  const cd = trim(countryCd);
  if (!cd) return '';
  const list = options ?? countryOptionsCache ?? [];
  const hit = list.find((o) => o.value === cd);
  return hit?.title ?? cd;
}

let b15OptionsCache: CodeSelectOption[] | null = null;
let b15OptionsLoadPromise: Promise<CodeSelectOption[]> | null = null;

export async function loadB15JurisdictionOptions(): Promise<CodeSelectOption[]> {
  if (b15OptionsCache) return b15OptionsCache;
  if (b15OptionsLoadPromise) return b15OptionsLoadPromise;

  b15OptionsLoadPromise = (async () => {
    const options: CodeSelectOption[] = [];
    try {
      const response = await baseCodeApi.getCodesByMainCode('CM', 'B15', undefined);
      const codes = (response as { codes?: unknown[] }).codes
        ?? (response as { data?: { codes?: unknown[] } }).data?.codes
        ?? [];
      for (const code of codes as Record<string, unknown>[]) {
        const value = trim(code.totalCode ?? code.TOTAL_CODE).toUpperCase();
        const title = trim(
          code.defaultCodeName ?? code.DEFAULT_CODE_NAME ?? code.codeName ?? code.CODE_NAME,
        );
        if (value && title) options.push({ title, value });
      }
      options.sort((a, b) => a.title.localeCompare(b.title));
    } catch (e) {
      console.warn('[notice-corporate formOptions] B15 load failed:', e);
    }
    b15OptionsCache = options;
    return options;
  })();

  return b15OptionsLoadPromise;
}

/** 단일 Contact no 문자열 → country / area / mobile (레거시 호환) */
export function parseLegacyContactPhone(raw: string): {
  countryCd: string;
  areaCode: string;
  mobileNo: string;
} {
  const s = trim(raw);
  if (!s) return { countryCd: '', areaCode: '', mobileNo: '' };
  const parts = s.split(/\s+/).filter(Boolean);
  if (parts.length >= 3 && /^\d{1,4}$/.test(parts[0]) && /^\d{1,4}$/.test(parts[1])) {
    return { countryCd: '', areaCode: parts[0], mobileNo: parts.slice(1).join(' ') };
  }
  if (parts.length >= 2 && /^\d{1,4}$/.test(parts[0])) {
    return { countryCd: '', areaCode: parts[0], mobileNo: parts.slice(1).join(' ') };
  }
  return { countryCd: '', areaCode: '', mobileNo: s };
}

export function formatContactNoDisplay(
  countryCd: string,
  areaCode: string,
  mobileNo: string,
): string {
  return [trim(areaCode), trim(mobileNo)].filter(Boolean).join(' ').trim();
}
